import type { StoredEvent } from './protocol.ts';

export type ChatItem =
  | { id: string; ts: number; type: 'user'; text: string; images: { id: string }[] }
  | { id: string; ts: number; type: 'assistant'; text: string; streaming: boolean }
  | {
      id: string;
      ts: number;
      type: 'tool';
      tool: string;
      summary: string;
      state: 'running' | 'ok' | 'fail';
      detail?: string;
    }
  | {
      id: string;
      ts: number;
      type: 'video';
      videoId: string;
      mime: string;
      caption: string;
      width?: number;
      height?: number;
    }
  | {
      id: string;
      ts: number;
      type: 'shot';
      imageId: string;
      caption: string;
      width?: number;
      height?: number;
    }
  | { id: string; ts: number; type: 'note'; text: string; level: 'info' | 'error' };

export type ToolItem = Extract<ChatItem, { type: 'tool' }>;

/**
 * Consecutive tool calls render as one counted row. Anything the agent says
 * to the reader — prose, a screenshot, a video — or any other non-tool item
 * starts a new row.
 */
export type ChatBlock =
  | Exclude<ChatItem, { type: 'tool' }>
  | { id: string; type: 'tools'; items: ToolItem[] };

/**
 * What the transcript says while a turn waits out the Claude usage limit.
 * The gateway holds the run itself, so this is a promise the phone does not
 * have to be awake to see kept.
 */
export function pauseNote(resumeAt: number | null): string {
  const head = 'Claude usage limit reached. This turn is paused, not lost';
  if (resumeAt === null) return `${head} - it runs again when the window reopens.`;
  const when = new Date(resumeAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${head} - it runs again at ${when}, when the 5-hour window resets.`;
}

export function groupChatItems(items: ChatItem[]): ChatBlock[] {
  const blocks: ChatBlock[] = [];
  for (const item of items) {
    if (item.type === 'tool') {
      const last = blocks[blocks.length - 1];
      if (last?.type === 'tools') {
        last.items.push(item);
        continue;
      }
      blocks.push({ id: item.id, type: 'tools', items: [item] });
      continue;
    }
    blocks.push(item);
  }
  return blocks;
}

export interface ChatState {
  items: ChatItem[];
  lastSeq: number;
  /** True between run.started and run.finished, so the composer can offer Stop. */
  running: boolean;
  /** Messages accepted but not yet handed to cursor-agent. */
  queued: number;
  /** Timestamp of the latest run.finished, so the wait clock can freeze. */
  lastRunEndedAt: number | null;
  /** messageId / callId -> index in `items`, since ids are only run-scoped. */
  byMessage: Record<string, number>;
  byCall: Record<string, number>;
}

export const emptyChat: ChatState = {
  items: [],
  lastSeq: 0,
  running: false,
  queued: 0,
  lastRunEndedAt: null,
  byMessage: {},
  byCall: {},
};

/**
 * Folds the persisted event log into what the screen shows.
 *
 * Deliberately a pure reducer over `(state, event)`: replaying the log from
 * seq 0 after a cold start and applying live events as they arrive go through
 * exactly the same code, so a reconnect can never render differently from a
 * fresh load.
 */
export function reduce(state: ChatState, stored: StoredEvent): ChatState {
  if (stored.seq <= state.lastSeq) return state;
  const { event, ts, seq } = stored;
  const next: ChatState = { ...state, lastSeq: seq };

  switch (event.kind) {
    case 'user.message':
      next.items = [
        ...state.items,
        {
          id: `u${seq}`,
          ts,
          type: 'user',
          text: event.text,
          images: (event.images ?? []).map((img) => ({ id: img.id })),
        },
      ];
      next.queued = state.queued + 1;
      return next;

    case 'run.started':
      next.running = true;
      next.queued = Math.max(0, state.queued - 1);
      return next;

    case 'run.finished':
      next.running = false;
      next.lastRunEndedAt = ts;
      if (!state.running) {
        // Cancelled (or failed) before it was handed to the agent: it never
        // left the queue, so this is the event that takes it off.
        next.queued = Math.max(0, state.queued - 1);
      }
      if (event.status === 'error' || event.status === 'cancelled') {
        next.items = [
          ...state.items,
          {
            id: `r${seq}`,
            ts,
            type: 'note',
            level: event.status === 'error' ? 'error' : 'info',
            text:
              event.status === 'cancelled'
                ? state.running
                  ? 'Stopped.'
                  : 'Removed from the queue.'
                : (event.error ?? 'Run failed.'),
          },
        ];
      }
      return next;

    case 'run.paused':
      // Not an ending: the turn stopped being in flight and went back to the
      // head of the queue to wait out the Claude 5-hour window.
      next.running = false;
      next.queued = state.queued + 1;
      next.items = [
        ...state.items,
        { id: `p${seq}`, ts, type: 'note', level: 'info', text: pauseNote(event.resumeAt) },
      ];
      return next;

    case 'run.resumed':
      next.items = [
        ...state.items,
        {
          id: `p${seq}`,
          ts,
          type: 'note',
          level: 'info',
          text:
            event.attempt > 1
              ? `Usage window reopened - trying this turn again (attempt ${event.attempt}).`
              : 'Usage window reopened - picking this turn back up.',
        },
      ];
      return next;

    case 'assistant.start':
      next.items = [...state.items, { id: `a${seq}`, ts, type: 'assistant', text: '', streaming: true }];
      next.byMessage = { ...state.byMessage, [event.messageId]: next.items.length - 1 };
      return next;

    case 'assistant.delta': {
      const idx = state.byMessage[event.messageId];
      const item = idx === undefined ? undefined : state.items[idx];
      if (item?.type !== 'assistant') return next;
      next.items = [...state.items];
      next.items[idx!] = { ...item, text: item.text + event.text };
      return next;
    }

    case 'assistant.end': {
      const idx = state.byMessage[event.messageId];
      const item = idx === undefined ? undefined : state.items[idx];
      if (item?.type !== 'assistant') return next;
      next.items = [...state.items];
      // The end event carries the authoritative text; trust it over the deltas.
      next.items[idx!] = { ...item, text: event.text || item.text, streaming: false };
      return next;
    }

    case 'tool.started':
      next.items = [
        ...state.items,
        { id: `t${seq}`, ts, type: 'tool', tool: event.tool, summary: event.summary, state: 'running' },
      ];
      next.byCall = { ...state.byCall, [event.callId]: next.items.length - 1 };
      return next;

    case 'tool.completed': {
      const idx = state.byCall[event.callId];
      const item = idx === undefined ? undefined : state.items[idx];
      const resolved: ChatItem = {
        id: item?.id ?? `t${seq}`,
        ts: item?.ts ?? ts,
        type: 'tool',
        tool: event.tool,
        summary: event.summary,
        state: event.ok ? 'ok' : 'fail',
        ...(event.detail ? { detail: event.detail } : {}),
      };
      if (item?.type !== 'tool') {
        next.items = [...state.items, resolved];
        next.byCall = { ...state.byCall, [event.callId]: next.items.length - 1 };
        return next;
      }
      next.items = [...state.items];
      next.items[idx!] = resolved;
      return next;
    }

    case 'agent.video':
      next.items = [
        ...state.items,
        {
          id: `v${seq}`,
          ts,
          type: 'video',
          videoId: event.video.id,
          mime: event.video.mime,
          caption: event.caption ?? '',
          ...(event.video.width ? { width: event.video.width } : {}),
          ...(event.video.height ? { height: event.video.height } : {}),
        },
      ];
      return next;

    case 'agent.shot':
      next.items = [
        ...state.items,
        {
          id: `s${seq}`,
          ts,
          type: 'shot',
          imageId: event.shot.id,
          caption: event.caption ?? '',
          ...(event.shot.width ? { width: event.shot.width } : {}),
          ...(event.shot.height ? { height: event.shot.height } : {}),
        },
      ];
      return next;

    case 'agent.info':
    case 'agent.error':
      next.items = [
        ...state.items,
        {
          id: `n${seq}`,
          ts,
          type: 'note',
          level: event.kind === 'agent.error' ? 'error' : 'info',
          text: event.text,
        },
      ];
      return next;
  }
}

export function reduceAll(state: ChatState, events: StoredEvent[]): ChatState {
  return events.reduce(reduce, state);
}
